import { UseQueryOptions } from '@tanstack/react-query';

import APIError from 'types/api_error';
import { TrackableItem } from 'types/trackables';
import { OffsetPaginated, OffsetPaginationParams, CursorPaginated, CursorPaginationParams  } from 'types/pagination';

import { useAPI } from 'hooks/api/useAPI';
import { useAuthenticatedQuery } from 'hooks/api/useAuthenticatedQuery';

export interface UseTrackableItemsParams {
  trackable_list_id?: string;
  trackable_item_ids?: string[];
  trackable_item_names?: string[];
  labels?: string[];
}

type UseTrackableItemsOptions = UseQueryOptions<OffsetPaginated<TrackableItem>, APIError> & {
  params?: UseTrackableItemsParams & OffsetPaginationParams;
};

export function useTrackableItems({
  queryKey = ['trackable-items'],
  params,
  ...options
}: UseTrackableItemsOptions = {}) {
  const { applicationID, client } = useAPI();

  const { trackable_list_id, ...otherParams } = params || {};

  const queryParams = {
    pagination: 'offset',
    page: 1,
    limit: 50,
    ...otherParams,
  };

  return useAuthenticatedQuery(
    [...queryKey, applicationID, trackable_list_id, queryParams],
    async () => {
      const { data } = await client.get(`/apps/${applicationID}/trackables/${trackable_list_id}/items`, {
        params: queryParams,
      });
      return data;
    },
    {
      enabled: Boolean(applicationID && trackable_list_id),
      ...options,
    }
  );
}

export interface UseTrackableItemsCrossListsParams {
  trackable_list_ids?: string[];
  trackable_item_ids?: string[];
  trackable_item_names?: string[];
  labels?: string[];
}

type UseTrackableItemsCrossListsOptions = UseQueryOptions<CursorPaginated<TrackableItem>, APIError> & {
  params?: UseTrackableItemsCrossListsParams & CursorPaginationParams;
};

export function useTrackableItemsCrossLists({
  queryKey = ['trackable-items-cross-lists'],
  params,
  ...options
}: UseTrackableItemsCrossListsOptions = {}) {
  const { applicationID, client } = useAPI();

  const queryParams = {
    pagination: 'cursor',
    limit: 50,
    ...params,
  } as const;

  return useAuthenticatedQuery(
    [...queryKey, applicationID, queryParams],
    async () => {
      const { data } = await client.get<CursorPaginated<TrackableItem>>(
        `/apps/${applicationID}/trackable_items`,
        {
          params: queryParams,
        }
      );
      return data;
    },
    {
      enabled: Boolean(applicationID),
      ...options,
    }
  );
}
